import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { Button, Row, InlineMessage, Icon } from '@appcues/sonar';
import { Input, Select } from 'ext/components/ui';
import { sortList } from 'ext/lib/sort-list';
import { Shape as ThemesShape, create as createTheme } from 'entities/themes';

const ThemeSelect = ({
  selectedThemeId,
  themes,
  onThemeChange,
  onCreateTheme,
}) => {
  const [themeCreateInstanceTag, setThemeCreateInstanceTag] = useState(null);
  const [newThemeName, setNewThemeName] = useState('');
  const [error, setError] = useState(null);

  const themeOptions = useMemo(
    () =>
      sortList(Object.values(themes)).map(({ id: value, name: label }) => ({
        label,
        value,
      })),
    [themes]
  );

  useEffect(() => {
    const newThemeId = Object.values(themes).find(
      theme => theme.sessionInstanceTag === themeCreateInstanceTag
    )?.id;
    if (newThemeId) {
      setNewThemeName('');
      setThemeCreateInstanceTag(null);
      onThemeChange({ value: newThemeId });
    }
  }, [themes, themeCreateInstanceTag, onThemeChange]);

  const handleCreateTheme = async event => {
    event.preventDefault();

    try {
      setError(null);
      onCreateTheme({
        name: newThemeName,
        isDefault: !Object.values(themes).some(theme => theme.isDefault),
        sessionInstanceTag: themeCreateInstanceTag,
      });
    } catch {
      setError('Failed to create theme. Please try again.');
    }
  };

  return themeCreateInstanceTag ? (
    <>
      <Row spacing="small">
        <Input
          id="newThemeName"
          onChange={e => setNewThemeName(e.target.value)}
          placeholder="Enter theme name"
          value={newThemeName}
        />
        <Button
          variant="primary"
          onClick={handleCreateTheme}
          disabled={!newThemeName.trim()}
        >
          Create
        </Button>
        <Button
          variant="tertiary"
          onClick={() => {
            setThemeCreateInstanceTag(null);
            setError(null);
          }}
        >
          <Icon icon={faXmark} />
        </Button>
      </Row>
      {error && <InlineMessage variant="error">{error}</InlineMessage>}
    </>
  ) : (
    <Select
      id="theme"
      onChange={onThemeChange}
      options={themeOptions}
      placeholder="Select theme"
      aria-label="Select theme"
      noOptionsMessage={() => 'No themes'}
      value={themeOptions.find(opt => opt.value === selectedThemeId) || null}
      menuButton={
        <Button
          variant="secondary"
          fluid
          onClick={() => setThemeCreateInstanceTag(Date.now())}
        >
          Create new theme
        </Button>
      }
    />
  );
};

ThemeSelect.propTypes = {
  selectedThemeId: PropTypes.string,
  themes: PropTypes.objectOf(ThemesShape),
  onThemeChange: PropTypes.func,
  onCreateTheme: PropTypes.func,
};

const mapDispatchToProps = {
  onCreateTheme: createTheme,
};

export default connect(null, mapDispatchToProps)(ThemeSelect);
